import * as ExcelJS from "exceljs";
import {
  addJustupBranding,
  setJustupMetadata,
} from "../../../../shared/utils/excel-branding.util";
import {
  CommandePilotageItem,
  PilotageEtatBreakdown,
  PilotageStats,
  PilotageTiersBreakdown,
} from "./commande-pilotage.types";

const C = {
  headerBg: "FF1B2A4A",
  headerFont: "FFFFFFFF",
  zebraBg: "FFF5F7FA",
  formulaBg: "FFFEFCE8", // jaune clair pour cellules dérivées (formules)
  border: "FFD0D5DD",
  red: "FFDC2626",
  orange: "FFF97316",
  green: "FF16A34A",
  titleBg: "FF1B2A4A",
  titleFont: "FFFFFFFF",
};

interface ExportData {
  mode: "fournisseur" | "client";
  items: CommandePilotageItem[];
  stats: PilotageStats;
  etatBreakdown: PilotageEtatBreakdown[];
  tiersBreakdown: PilotageTiersBreakdown[];
}

function colLetter(col: number): string {
  let s = "";
  let c = col;
  while (c > 0) {
    c--;
    s = String.fromCharCode(65 + (c % 26)) + s;
    c = Math.floor(c / 26);
  }
  return s;
}

function ref(col: number, row: number): string {
  return `${colLetter(col)}${row}`;
}

function styleHeader(cell: ExcelJS.Cell): void {
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  cell.font = { color: { argb: C.headerFont }, bold: true };
  cell.alignment = { vertical: "middle", horizontal: "left" };
  cell.border = {
    top: { style: "thin", color: { argb: C.border } },
    left: { style: "thin", color: { argb: C.border } },
    bottom: { style: "thin", color: { argb: C.border } },
    right: { style: "thin", color: { argb: C.border } },
  };
}

function styleFormula(cell: ExcelJS.Cell): void {
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.formulaBg },
  };
  cell.font = { italic: true };
}

function fmtTitle(ws: ExcelJS.Worksheet, label: string, span = 6): void {
  const r = ws.addRow([label]);
  r.height = 22;
  ws.mergeCells(r.number, 1, r.number, span);
  const cell = ws.getCell(r.number, 1);
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.titleBg },
  };
  cell.font = { color: { argb: C.titleFont }, bold: true, size: 13 };
  cell.alignment = { vertical: "middle", horizontal: "left", indent: 1 };
}

const eurFmt = '#,##0 "€"';
const pctFmt = "0.0 %";

export async function exportCommandePilotageExcel(
  data: ExportData,
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb, {
    subject:
      data.mode === "fournisseur"
        ? "Pilotage commandes fournisseurs"
        : "Pilotage commandes clients",
  });

  const tiersLabel = data.mode === "fournisseur" ? "Fournisseur" : "Client";
  const generatedAt = new Date().toLocaleString("fr-FR");

  // ─── Sheet 1 : Synthèse ───────────────────────────────────────────────
  {
    const ws = wb.addWorksheet("Synthèse");
    ws.columns = [
      { width: 32 },
      { width: 22 },
      { width: 60 },
    ];

    fmtTitle(
      ws,
      data.mode === "fournisseur"
        ? "Pilotage commandes fournisseurs"
        : "Pilotage commandes clients",
      3,
    );
    ws.addRow([`Généré le ${generatedAt}`]).getCell(1).font = {
      italic: true,
      color: { argb: "FF667085" },
    };
    ws.addRow([]);

    const headerRow = ws.addRow(["Indicateur", "Valeur", "Calcul"]);
    headerRow.eachCell((c) => styleHeader(c));

    // Note: lignes "Valeur" en formules dérivées de la liste détail (sheet 4)
    // pour que l'utilisateur puisse re-saisir / éditer les données et voir
    // les KPIs se recalculer.
    // La feuille "Détail" sera la sheet 4. On pré-référence ses colonnes :
    //   col B = totalHT, col F = daysLate, col G = enRetard (1/0)
    const det = "'Détail'!";
    const lastRow = `${data.items.length + 1}`; // +1 = header

    const addKpi = (
      label: string,
      formula: string | number | null,
      explain: string,
      numFmt?: string,
    ) => {
      const r = ws.addRow([label, "", explain]);
      r.getCell(1).font = { bold: true };
      r.getCell(3).font = { color: { argb: "FF667085" }, italic: true };
      const valCell = r.getCell(2);
      if (typeof formula === "string") {
        valCell.value = { formula } as any;
        styleFormula(valCell);
      } else {
        valCell.value = formula ?? "";
      }
      if (numFmt) valCell.numFmt = numFmt;
      valCell.alignment = { horizontal: "right" };
    };

    addKpi(
      "Commandes ouvertes",
      `COUNTA(${det}A2:A${lastRow})`,
      "Nb total de commandes en cours (toutes étapes)",
      "0",
    );
    addKpi(
      "Montant HT total",
      `SUM(${det}H2:H${lastRow})`,
      "Σ totalHT (col Détail H)",
      eurFmt,
    );
    addKpi(
      "Nb en retard",
      `SUMIF(${det}G2:G${lastRow},">0")`,
      "Nb de commandes avec daysLate > 0",
      "0",
    );
    addKpi(
      "Montant HT en retard",
      `SUMIF(${det}G2:G${lastRow},">0",${det}H2:H${lastRow})`,
      "Σ totalHT des commandes en retard",
      eurFmt,
    );
    addKpi(
      "Retard moyen (jours)",
      `IFERROR(AVERAGEIF(${det}G2:G${lastRow},">0"),0)`,
      "Moyenne du retard sur les commandes en retard uniquement",
      "0",
    );
    addKpi(
      "Retard max (jours)",
      `IFERROR(MAX(${det}G2:G${lastRow}),0)`,
      "Pire retard constaté",
      "0",
    );
    addKpi(
      "% en retard",
      `IFERROR(SUMIF(${det}G2:G${lastRow},">0")/COUNTA(${det}A2:A${lastRow}),0)`,
      "Nb retard / Nb total",
      pctFmt,
    );
    addKpi(
      "Nb articles total",
      `SUM(${det}I2:I${lastRow})`,
      "Σ nbLignes (col Détail I)",
      "0",
    );

    await addJustupBranding(wb, ws, 3);
  }

  // ─── Sheet 2 : Par étape ──────────────────────────────────────────────
  {
    const ws = wb.addWorksheet("Par étape");
    ws.columns = [
      { width: 30 },
      { width: 12 },
      { width: 18 },
      { width: 14 },
      { width: 14 },
    ];
    fmtTitle(ws, "Répartition par étape", 5);
    ws.addRow([]);
    const header = ws.addRow([
      "Étape",
      "Nb",
      "Montant HT",
      "En retard",
      "% retard",
    ]);
    header.eachCell((c) => styleHeader(c));

    for (const e of data.etatBreakdown) {
      const r = ws.addRow([e.etatLabel, e.count, e.totalHT, e.nbLate, ""]);
      r.getCell(3).numFmt = eurFmt;
      // Formule : % retard = nbLate / count
      const row = r.number;
      r.getCell(5).value = {
        formula: `IFERROR(${ref(4, row)}/${ref(2, row)},0)`,
      } as any;
      r.getCell(5).numFmt = pctFmt;
      styleFormula(r.getCell(5));
      // Couleur conditionnelle sur "% retard"
      if (e.pctLate >= 0.5) {
        r.getCell(5).font = { color: { argb: C.red }, bold: true };
      } else if (e.pctLate >= 0.2) {
        r.getCell(5).font = { color: { argb: C.orange }, bold: true };
      }
    }

    // Totaux
    const totalRow = ws.addRow([
      "TOTAL",
      "",
      "",
      "",
      "",
    ]);
    const tr = totalRow.number;
    const firstData = 4; // ligne 4 = première donnée (titre + vide + header)
    const lastData = tr - 1;
    totalRow.getCell(1).font = { bold: true };
    totalRow.getCell(2).value = {
      formula: `SUM(${ref(2, firstData)}:${ref(2, lastData)})`,
    } as any;
    totalRow.getCell(3).value = {
      formula: `SUM(${ref(3, firstData)}:${ref(3, lastData)})`,
    } as any;
    totalRow.getCell(3).numFmt = eurFmt;
    totalRow.getCell(4).value = {
      formula: `SUM(${ref(4, firstData)}:${ref(4, lastData)})`,
    } as any;
    totalRow.getCell(5).value = {
      formula: `IFERROR(${ref(4, tr)}/${ref(2, tr)},0)`,
    } as any;
    totalRow.getCell(5).numFmt = pctFmt;
    [2, 3, 4, 5].forEach((i) => styleFormula(totalRow.getCell(i)));

    await addJustupBranding(wb, ws, 5);
  }

  // ─── Sheet 3 : Top tiers ──────────────────────────────────────────────
  {
    const ws = wb.addWorksheet(`Par ${tiersLabel.toLowerCase()}`);
    ws.columns = [
      { width: 36 },
      { width: 10 },
      { width: 18 },
      { width: 14 },
      { width: 14 },
      { width: 14 },
      { width: 16 },
    ];
    fmtTitle(ws, `Pilotage par ${tiersLabel.toLowerCase()}`, 7);
    ws.addRow([]);
    const header = ws.addRow([
      tiersLabel,
      "Cmd",
      "Montant HT",
      "En retard",
      "Retard max (j)",
      "% retard",
      "Dernière cmd",
    ]);
    header.eachCell((c) => styleHeader(c));

    for (const t of data.tiersBreakdown) {
      const r = ws.addRow([
        t.tiersDenomination,
        t.count,
        t.totalHT,
        t.nbLate,
        t.maxDaysLate || 0,
        "",
        t.lastCommandeDate ? new Date(t.lastCommandeDate) : "",
      ]);
      r.getCell(3).numFmt = eurFmt;
      r.getCell(7).numFmt = "dd/mm/yyyy";
      const row = r.number;
      // Formule % retard
      r.getCell(6).value = {
        formula: `IFERROR(${ref(4, row)}/${ref(2, row)},0)`,
      } as any;
      r.getCell(6).numFmt = pctFmt;
      styleFormula(r.getCell(6));
    }
    await addJustupBranding(wb, ws, 7);
  }

  // ─── Sheet 4 : Détail ─────────────────────────────────────────────────
  {
    const ws = wb.addWorksheet("Détail");
    ws.columns = [
      { width: 28 }, // A: tiers
      { width: 14 }, // B: numero
      { width: 22 }, // C: etat
      { width: 14 }, // D: dateCreation
      { width: 14 }, // E: dateLivraisonPrevue
      { width: 12 }, // F: daysLate
      { width: 12 }, // G: enRetard (1/0) — formule
      { width: 16 }, // H: totalHT
      { width: 10 }, // I: nbLignes
      { width: 16 }, // J: totalTTC
    ];
    const header = ws.addRow([
      tiersLabel,
      "Numéro",
      "Étape",
      "Création",
      "Livraison prévue",
      "Retard (j)",
      "En retard (0/1)",
      "Total HT",
      "Articles",
      "Total TTC",
    ]);
    header.eachCell((c) => styleHeader(c));

    for (const c of data.items) {
      const r = ws.addRow([
        c.tiersDenomination,
        c.numero,
        c.etatLabel,
        c.dateCreation ? new Date(c.dateCreation) : "",
        c.dateLivraisonPrevue ? new Date(c.dateLivraisonPrevue) : "",
        c.daysLate,
        "", // formule en retard
        c.totalHT,
        c.nbLignes,
        c.totalTTC,
      ]);
      const row = r.number;
      r.getCell(4).numFmt = "dd/mm/yyyy";
      r.getCell(5).numFmt = "dd/mm/yyyy";
      r.getCell(8).numFmt = eurFmt;
      r.getCell(10).numFmt = eurFmt;
      // Formule "En retard" = 1 si daysLate > 0
      r.getCell(7).value = {
        formula: `IF(${ref(6, row)}>0,1,0)`,
      } as any;
      styleFormula(r.getCell(7));
      // Coloration ligne en retard
      if (c.daysLate > 0) {
        r.getCell(6).font = { color: { argb: C.red }, bold: true };
      }
    }

    // Auto-filter sur le tableau détail
    ws.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: data.items.length + 1, column: 10 },
    };
    await addJustupBranding(wb, ws, 10);
  }

  // Téléchargement
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `pilotage-commandes-${data.mode}-${new Date()
    .toISOString()
    .slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
